import React, { useState, useEffect } from 'react';
import { 
	Card, 
	Form, 
	Input, 
	Select, 
	DatePicker, 
	Button, 
	Row, 
	Col, 
	Space,
	Checkbox,
	InputNumber,
	Collapse,
	Tag,
	Tooltip,
	Badge
} from 'antd';
import {
	SearchOutlined,
	FilterOutlined,
	ClearOutlined,
	UserOutlined,
	MailOutlined,
	CalendarOutlined,
	CrownOutlined,
	SafetyOutlined,
	VideoCameraOutlined,
	CheckCircleOutlined,
	StopOutlined,
	ExclamationCircleOutlined,
	GlobalOutlined,
	DownOutlined,
	UpOutlined
} from '@ant-design/icons';
import moment from 'moment';
import './UserFilters.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const UserFilters = ({ 
	onFiltersChange, 
	loading, 
	totalFiltered,
	totalUsers,
	initialFilters = {}
}) => {
	const [form] = Form.useForm();
	const [activeFilters, setActiveFilters] = useState({});
	const [expandedPanels, setExpandedPanels] = useState(['basic']);
	const [quickFilters, setQuickFilters] = useState([]);

	// Quick filter presets
	const quickFilterPresets = [
		{
			key: 'active-users',
			label: 'Active Users',
			icon: <CheckCircleOutlined />,
			color: 'success',
			filters: { status: ['active'] }
		},
		{
			key: 'new-users',
			label: 'New Users (7 days)',
			icon: <UserOutlined />,
			color: 'blue',
			filters: { 
				createdAt: [moment().subtract(7, 'days'), moment()]
			}
		},
		{
			key: 'verified-users',
			label: 'Verified Users',
			icon: <CheckCircleOutlined />,
			color: 'gold',
			filters: { verified: true }
		},
		{
			key: 'streamers',
			label: 'Streamers',
			icon: <VideoCameraOutlined />,
			color: 'purple',
			filters: { role: ['streamer'] }
		},
		{
			key: 'banned-users',
			label: 'Banned Users',
			icon: <StopOutlined />,
			color: 'red',
			filters: { status: ['banned'] }
		},
		{
			key: 'inactive-users',
			label: 'Inactive (30+ days)',
			icon: <ExclamationCircleOutlined />,
			color: 'orange',
			filters: { 
				lastLogin: [null, moment().subtract(30, 'days')]
			}
		}
	];

	// Country options (sample data)
	const countryOptions = [
		'United States', 'Canada', 'United Kingdom', 'Germany', 'France',
		'Japan', 'South Korea', 'China', 'Australia', 'Brazil',
		'Mexico', 'India', 'Russia', 'Italy', 'Spain'
	];

	// Language options
	const languageOptions = [
		{ value: 'en', label: 'English' },
		{ value: 'zh', label: 'Chinese' },
		{ value: 'ja', label: 'Japanese' },
		{ value: 'ko', label: 'Korean' },
		{ value: 'es', label: 'Spanish' },
		{ value: 'fr', label: 'French' },
		{ value: 'de', label: 'German' },
		{ value: 'pt', label: 'Portuguese' },
		{ value: 'ru', label: 'Russian' },
		{ value: 'it', label: 'Italian' }
	];

	useEffect(() => {
		// Set initial filters
		if (Object.keys(initialFilters).length > 0) {
			form.setFieldsValue(initialFilters);
			setActiveFilters(initialFilters);
		}
	}, [initialFilters, form]);

	// Handle form values change
	const handleValuesChange = (changedValues, allValues) => {
		// Remove empty values
		const cleanedValues = Object.keys(allValues).reduce((acc, key) => {
			const value = allValues[key];
			if (value !== undefined && value !== null && value !== '' && 
				(!Array.isArray(value) || value.length > 0)) {
				acc[key] = value;
			}
			return acc;
		}, {});

		setActiveFilters(cleanedValues);
		onFiltersChange(cleanedValues);
	};

	// Handle quick filter click
	const handleQuickFilter = (preset) => {
		const isActive = quickFilters.includes(preset.key);
		
		if (isActive) {
			// Remove quick filter
			const newQuickFilters = quickFilters.filter(key => key !== preset.key);
			setQuickFilters(newQuickFilters);
			
			// Remove preset filters from form
			const currentValues = form.getFieldsValue();
			const newValues = { ...currentValues };
			
			Object.keys(preset.filters).forEach(key => {
				if (Array.isArray(preset.filters[key])) {
					newValues[key] = [];
				} else {
					delete newValues[key];
				}
			});
			
			form.setFieldsValue(newValues);
			handleValuesChange({}, newValues);
		} else {
			// Add quick filter
			const newQuickFilters = [...quickFilters, preset.key];
			setQuickFilters(newQuickFilters);
			
			// Apply preset filters
			const currentValues = form.getFieldsValue();
			const newValues = { ...currentValues, ...preset.filters };
			
			form.setFieldsValue(newValues);
			handleValuesChange({}, newValues);
		}
	};

	// Clear all filters
	const handleClearFilters = () => {
		form.resetFields();
		setActiveFilters({});
		setQuickFilters([]);
		onFiltersChange({});
	};

	// Handle collapse change
	const handleCollapseChange = (keys) => {
		setExpandedPanels(keys);
	};

	// Get active filter count
	const getActiveFilterCount = () => {
		return Object.keys(activeFilters).length;
	};

	// Render filter summary
	const renderFilterSummary = () => {
		const filterCount = getActiveFilterCount();
		if (filterCount === 0) return null;

		return (
			<div className="filter-summary">
				<div className="summary-info">
					<Badge count={filterCount} className="filter-badge">
						<FilterOutlined className="summary-icon" />
					</Badge>
					<span className="summary-text">
						{totalFiltered !== undefined ? (
							<>Showing {totalFiltered.toLocaleString()} of {totalUsers.toLocaleString()} users</>
						) : (
							<>{filterCount} filter{filterCount > 1 ? 's' : ''} active</>
						)}
					</span>
				</div>
				<Button 
					type="text" 
					size="small" 
					icon={<ClearOutlined />}
					onClick={handleClearFilters}
					className="clear-filters-btn"
				>
					Clear All
				</Button>
			</div>
		);
	};

	return (
		<div className="user-filters-wrapper">
			<Card className="filters-card">
				{/* Quick Filters */}
				<div className="quick-filters-section">
					<div className="section-title">
						<FilterOutlined className="section-icon" />
						Quick Filters
					</div>
					<div className="quick-filters">
						{quickFilterPresets.map(preset => (
							<Tag
								key={preset.key}
								color={quickFilters.includes(preset.key) ? preset.color : 'default'}
								className={`quick-filter-tag ${quickFilters.includes(preset.key) ? 'active' : ''}`}
								onClick={() => handleQuickFilter(preset)}
							>
								{preset.icon}
								{preset.label}
							</Tag>
						))}
					</div>
				</div>

				{/* Filter Summary */}
				{renderFilterSummary()}

				{/* Advanced Filters */}
				<Form
					form={form}
					layout="vertical"
					onValuesChange={handleValuesChange}
					className="filters-form"
				>
					<Collapse 
						activeKey={expandedPanels}
						onChange={handleCollapseChange}
						className="filters-collapse"
						expandIcon={({ isActive }) => (
							isActive ? <UpOutlined /> : <DownOutlined />
						)}
					>
						{/* Basic Filters */}
						<Panel header="Basic Filters" key="basic" className="filter-panel">
							<Row gutter={[16, 16]}>
								{/* Search */}
								<Col xs={24} md={12} lg={8}>
									<Form.Item name="search" label="Search">
										<Input
											prefix={<SearchOutlined />}
											placeholder="Username, email, or display name"
											allowClear
										/>
									</Form.Item>
								</Col>
								{/* Role */}
								<Col xs={24} md={12} lg={8}>
									<Form.Item name="role" label="Role">
										<Select
											mode="multiple"
											placeholder="Select roles"
											allowClear
										>
											<Option value="user">
												<UserOutlined /> User
											</Option>
											<Option value="streamer">
												<VideoCameraOutlined /> Streamer
											</Option>
											<Option value="moderator">
												<SafetyOutlined /> Moderator
											</Option>
											<Option value="admin">
												<CrownOutlined /> Admin
											</Option>
										</Select>
									</Form.Item>
								</Col>

								{/* Status */}
								<Col xs={24} md={12} lg={8}>
									<Form.Item name="status" label="Status">
										<Select
											mode="multiple"
											placeholder="Select status"
											allowClear
										>
											<Option value="active">
												<CheckCircleOutlined style={{ color: 'var(--success)' }} /> Active
											</Option>
											<Option value="inactive">
												<StopOutlined style={{ color: 'var(--text-tertiary)' }} /> Inactive
											</Option>
											<Option value="banned">
												<ExclamationCircleOutlined style={{ color: 'var(--accent)' }} /> Banned
											</Option>
											<Option value="pending">
												<ExclamationCircleOutlined style={{ color: 'var(--warning)' }} /> Pending
											</Option>
										</Select>
									</Form.Item>
								</Col>

								{/* Verified */}
								{/* <Col xs={24} md={12} lg={8}>
									<Form.Item name="verified" label="Verification">
										<Select placeholder="Verification status" allowClear>
											<Option value={true}>Verified</Option>
											<Option value={false}>Unverified</Option>
										</Select>
									</Form.Item>
								</Col> */}

								{/* Email Verified */}
								{/* <Col xs={24} md={12} lg={8}>
									<Form.Item name="emailVerified" label="Email Status">
										<Select placeholder="Email verification" allowClear>
											<Option value={true}>Email Verified</Option>
											<Option value={false}>Email Unverified</Option>
										</Select>
									</Form.Item>
								</Col> */}

								{/* Two Factor Auth */}
								{/* <Col xs={24} md={12} lg={8}>
									<Form.Item name="twoFactorAuth" label="2FA Status">
										<Select placeholder="Two-factor auth" allowClear>
											<Option value={true}>2FA Enabled</Option>
											<Option value={false}>2FA Disabled</Option>
										</Select>
									</Form.Item>
								</Col> */}
							</Row>
						</Panel>
					</Collapse>

					{/* Action Buttons */}
					<div className="filter-actions">
						<Space>
							<Button 
								type="default" 
								icon={<ClearOutlined />}
								onClick={handleClearFilters}
								disabled={getActiveFilterCount() === 0}
							>
								Clear Filters
							</Button>
							{/* <Tooltip title="Export filtered results">
								<Button type="primary" icon={<GlobalOutlined />}>
									Export Results
								</Button>
							</Tooltip> */}
						</Space>
					</div>
				</Form>
			</Card>
		</div>
	);
};

export default UserFilters;

						// {/* Date Filters */}
						// <Panel header="Date Filters" key="dates" className="filter-panel">
						// 	<Row gutter={[16, 16]}>
						// 		{/* Created Date */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item name="createdAt" label="Registration Date">
						// 				<RangePicker
						// 					style={{ width: '100%' }}
						// 					placeholder={['From date', 'To date']}
						// 					format="YYYY-MM-DD"
						// 				/>
						// 			</Form.Item>
						// 		</Col>

						// 		{/* Last Login */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item name="lastLogin" label="Last Login">
						// 				<RangePicker
						// 					style={{ width: '100%' }}
						// 					placeholder={['From date', 'To date']}
						// 					format="YYYY-MM-DD"
						// 					showTime
						// 				/>
						// 			</Form.Item>
						// 		</Col>

						// 		{/* Age Range */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item label="Age Range">
						// 				<Input.Group compact>
						// 					<Form.Item name="minAge" noStyle>
						// 						<InputNumber
						// 							placeholder="Min age"
						// 							min={13}
						// 							max={120}
						// 							style={{ width: '50%' }}
						// 						/>
						// 					</Form.Item>
						// 					<Form.Item name="maxAge" noStyle>
						// 						<InputNumber
						// 							placeholder="Max age"
						// 							min={13}
						// 							max={120}
						// 							style={{ width: '50%' }}
						// 						/>
						// 					</Form.Item>
						// 				</Input.Group>
						// 			</Form.Item>
						// 		</Col>

						// 		{/* Account Age (days) */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item label="Account Age (days)">
						// 				<Input.Group compact>
						// 					<Form.Item name="minAccountAge" noStyle>
						// 						<InputNumber
						// 							placeholder="Min days"
						// 							min={0}
						// 							style={{ width: '50%' }}
						// 						/>
						// 					</Form.Item>
						// 					<Form.Item name="maxAccountAge" noStyle>
						// 						<InputNumber
						// 							placeholder="Max days"
						// 							min={0}
						// 							style={{ width: '50%' }}
						// 						/>
						// 					</Form.Item>
						// 				</Input.Group>
						// 			</Form.Item>
						// 		</Col>
						// 	</Row>
						// </Panel>

						// {/* Location & Language */}
						// <Panel header="Location & Language" key="location" className="filter-panel">
						// 	<Row gutter={[16, 16]}>
						// 		{/* Country */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item name="country" label="Country">
						// 				<Select
						// 					mode="multiple"
						// 					placeholder="Select countries"
						// 					allowClear
						// 					showSearch
						// 					filterOption={(input, option) =>
						// 						option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						// 					}
						// 				>
						// 					{countryOptions.map(country => (
						// 						<Option key={country} value={country}>
						// 							{country}
						// 						</Option>
						// 					))}
						// 				</Select>
						// 			</Form.Item>
						// 		</Col>

						// 		{/* Language */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item name="language" label="Language">
						// 				<Select
						// 					mode="multiple"
						// 					placeholder="Select languages"
						// 					allowClear
						// 				>
						// 					{languageOptions.map(lang => (
						// 						<Option key={lang.value} value={lang.value}>
						// 							{lang.label}
						// 						</Option>
						// 					))}
						// 				</Select>
						// 			</Form.Item>
						// 		</Col>

						// 		{/* City */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item name="city" label="City">
						// 				<Input
						// 					placeholder="Enter city name"
						// 					allowClear
						// 				/>
						// 			</Form.Item>
						// 		</Col>

						// 		{/* Timezone */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item name="timezone" label="Timezone">
						// 				<Select
						// 					placeholder="Select timezone"
						// 					allowClear
						// 					showSearch
						// 				>
						// 					<Option value="UTC">UTC</Option>
						// 					<Option value="America/New_York">Eastern Time</Option>
						// 					<Option value="America/Chicago">Central Time</Option>
						// 					<Option value="America/Denver">Mountain Time</Option>
						// 					<Option value="America/Los_Angeles">Pacific Time</Option>
						// 					<Option value="Europe/London">GMT</Option>
						// 					<Option value="Europe/Paris">CET</Option>
						// 					<Option value="Asia/Tokyo">JST</Option>
						// 					<Option value="Asia/Shanghai">CST</Option>
						// 					<Option value="Asia/Seoul">KST</Option>
						// 				</Select>
						// 			</Form.Item>
						// 		</Col>
						// 	</Row>
						// </Panel>

						// {/* Activity Filters */}
						// <Panel header="Activity Filters" key="activity" className="filter-panel">
						// 	<Row gutter={[16, 16]}>
						// 		{/* Stream Count */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item label="Stream Count">
						// 				<Input.Group compact>
						// 					<Form.Item name="minStreams" noStyle>
						// 						<InputNumber
						// 							placeholder="Min streams"
						// 							min={0}
						// 							style={{ width: '50%' }}
						// 						/>
						// 					</Form.Item>
						// 					<Form.Item name="maxStreams" noStyle>
						// 						<InputNumber
						// 							placeholder="Max streams"
						// 							min={0}
						// 							style={{ width: '50%' }}
						// 						/>
						// 					</Form.Item>
						// 				</Input.Group>
						// 			</Form.Item>
						// 		</Col>

						// 		{/* Follower Count */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item label="Follower Count">
						// 				<Input.Group compact>
						// 					<Form.Item name="minFollowers" noStyle>
						// 						<InputNumber
						// 							placeholder="Min followers"
						// 							min={0}
						// 							style={{ width: '50%' }}
						// 						/>
						// 					</Form.Item>
						// 					<Form.Item name="maxFollowers" noStyle>
						// 						<InputNumber
						// 							placeholder="Max followers"
						// 							min={0}
						// 							style={{ width: '50%' }}
						// 						/>
						// 					</Form.Item>
						// 				</Input.Group>
						// 			</Form.Item>
						// 		</Col>

						// 		{/* Login Frequency */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item name="loginFrequency" label="Login Frequency">
						// 				<Select placeholder="Select frequency" allowClear>
						// 					<Option value="daily">Daily (last 7 days)</Option>
						// 					<Option value="weekly">Weekly (last 30 days)</Option>
						// 					<Option value="monthly">Monthly (last 90 days)</Option>
						// 					<Option value="rarely">Rarely (90+ days ago)</Option>
						// 					<Option value="never">Never logged in</Option>
						// 				</Select>
						// 			</Form.Item>
						// 		</Col>

						// 		{/* Online Status */}
						// 		<Col xs={24} md={12}>
						// 			<Form.Item name="onlineStatus" label="Online Status">
						// 				<Select placeholder="Select online status" allowClear>
						// 					<Option value="online">Currently Online</Option>
						// 					<Option value="offline">Currently Offline</Option>
						// 					<Option value="away">Away</Option>
						// 					<Option value="busy">Busy</Option>
						// 				</Select>
						// 			</Form.Item>
						// 		</Col>
						// 	</Row>
						// </Panel>

						// {/* Privacy Settings */}
						// <Panel header="Privacy Settings" key="privacy" className="filter-panel">
						// 	<Row gutter={[16, 16]}>
						// 		<Col span={24}>
						// 			<Form.Item name="privacySettings" label="Privacy Options">
						// 				<Checkbox.Group>
						// 					<Row gutter={[16, 8]}>
						// 						<Col xs={24} sm={12} md={8}>
						// 							<Checkbox value="profilePrivate">Private Profile</Checkbox>
						// 						</Col>
						// 						<Col xs={24} sm={12} md={8}>
						// 							<Checkbox value="allowMessages">Allow Messages</Checkbox>
						// 						</Col>
						// 						<Col xs={24} sm={12} md={8}>
						// 							<Checkbox value="showOnlineStatus">Show Online Status</Checkbox>
						// 						</Col>
						// 						<Col xs={24} sm={12} md={8}>
						// 							<Checkbox value="emailNotifications">Email Notifications</Checkbox>
						// 						</Col>
						// 						<Col xs={24} sm={12} md={8}>
						// 							<Checkbox value="pushNotifications">Push Notifications</Checkbox>
						// 						</Col>
						// 						<Col xs={24} sm={12} md={8}>
						// 							<Checkbox value="marketingEmails">Marketing Emails</Checkbox>
						// 						</Col>
						// 					</Row>
						// 				</Checkbox.Group>
						// 			</Form.Item>
						// 		</Col>
						// 	</Row>
						// </Panel>
